import { useNewsCategoryLabels } from '@/context/newsCategoryLabelsContext';
import { getNewsCategoryLabel } from '@/util/storyblok/news-categories';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import type { ReactElement, Ref, RefObject } from 'react';
import { useRef, useState } from 'react';
import { useScrollTransition } from 'util/react-transition-component';
import type { ArticleHeaderProps, ArticleHeaderRef } from './ArticleHeader.data';
import styles from './ArticleHeader.module.scss';
import { setupTransitionInTimeline } from './ArticleHeader.transitions';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import IconButton from '../../atoms/icon-button/IconButton';
import Image from '../../atoms/image/Image';
import sharedVariables from '../../../data/shared-variable/shared-variables.json';
import { useMobileViewport } from '../../../hooks/useMobileViewport';
import { formatDate } from '../../../util/dates.utils';
import { protocolRelativeToAbsoluteUrl } from '../../../util/url.utils';
import type { CtaRefType } from '../../../util/cta/cta.data';
import useIsomorphicLayoutEffect from '../../../hooks/useIsomorphicLayoutEffect';
import { useClientOnly } from '../../../hooks/useClientOnly';
import { addImageInVideoObject, hasVideo } from '../../../util/storyblok/data.utils';
import Video from '../../atoms/video/Video';
import { useGlobalStore } from '../../../stores/global';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import { useCanPlayWebmContext } from '../../../context/canPlayWebmContext';
import { useStoryblokEditorContext } from '../../unlisted/StoryblokEditor';

function ArticleHeader({
  className,
  blok,
  canTransitionIn,
  ref,
}: ArticleHeaderProps & { ref?: Ref<ArticleHeaderRef> }): ReactElement {
  const { locale } = useRouter();
  const categoryLabels = useNewsCategoryLabels();
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  const titleRef = useRef<HTMLHeadingElement>(null);
  const categoryRef = useRef<HTMLDivElement>(null);
  const dateRef = useRef<HTMLTimeElement>(null);
  const headerButtonRef = useRef<CtaRefType>(null);
  const imageHolder = useRef<HTMLDivElement>(null);
  const gifRef = useRef<HTMLVideoElement>(null);

  const isClientOnly = useClientOnly();
  const lineRef = useRef<HTMLHRElement>(null);
  const shareTitleRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<CtaRefType>(null);
  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768);
  const [formattedDate, setFormattedDate] = useState<string | null | undefined>(blok.publish_date!);
  const { canPlayWebm } = useCanPlayWebmContext();
  const { isInEditorMode } = useStoryblokEditorContext();

  const store = useGlobalStore();
  const { globalSettings } = store;

  useIsomorphicLayoutEffect(() => {
    setFormattedDate(formatDate(blok.publish_date!, 'D MMM YYYY', locale));
  }, [blok.publish_date, locale]);

  useScrollTransition({
    scroll: {
      canTransitionIn,
      trigger: elementRef,
    },
    setupOptions: () => ({
      refs: {
        elementRef,
        titleRef,
        categoryRef,
        dateRef,
        headerButtonRef,
        imageHolder,
        gifRef,
        lineRef,
        shareTitleRef,
        buttonRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const [hasCopiedLink, setHasCopiedLink] = useState(false);

  const copyToClipboard = () => {
    if (typeof window !== 'undefined') {
      if (!navigator.clipboard) return;
      navigator.clipboard.writeText(window.location.href);
      setHasCopiedLink(true);
      setTimeout(() => setHasCopiedLink(false), 3000);
    }
  };

  const shareWebsite = () => {
    if (navigator.share !== undefined) {
      const title = 'Dentsu Creative';
      const text = 'Check this out!';
      const url = window.location.href;

      navigator
        .share({
          title,
          text,
          url,
        })
        .catch((error) => {
          console.error(error);
          copyToClipboard();
        });
    } else {
      copyToClipboard();
    }
  };

  const { key_image, key_video } = blok;

  let src;
  let srcDesktop;
  if (key_image && key_image?.length > 0) {
    src = protocolRelativeToAbsoluteUrl(key_image[0].image_portrait);
    srcDesktop = protocolRelativeToAbsoluteUrl(key_image[0].image_landscape);

    if (!src) {
      src = protocolRelativeToAbsoluteUrl(key_image[0].image_landscape);
    }
  }

  const { scrollIntoView } = useScrollProviderContext() as ScrollProviderContextType;

  let media = <></>;

  if (key_video && hasVideo(key_video)) {
    media = (
      <div ref={imageHolder} className={styles.videoHolder}>
        <Video
          blok={addImageInVideoObject(key_video[0], srcDesktop || src)}
          objectFit="cover"
          controls
          pauseWhenOutViewport
          playButton
          innerTransition={{
            type: 'parallax',
            config: {
              speed: -2,
            },
          }}
          muted={false}
          className={classNames(styles.video, 'abs-fill')}
        />
      </div>
    );
  } else if (key_image?.[0]?.animated_image?.filename && canPlayWebm) {
    media = (
      <video
        ref={gifRef}
        src={key_image?.[0]?.animated_image.filename}
        autoPlay
        muted
        controls={false}
        playsInline
        loop
        className={classNames(styles.animatedImage)}
      />
    );
  } else if (key_image?.[0] && src) {
    media = (
      <div ref={imageHolder} className={styles.imageHolder}>
        {isInEditorMode && (src.endsWith('.gif') || src.endsWith('.GIF')) && (
          <div className={styles.noGifWarning}>
            <p>Dont upload gifs here!</p>
            <p>
              Their size is too large, instead use the <i>Animated Image</i> field to upload a webm
              format
            </p>
          </div>
        )}
        <Image
          src={src}
          srcDesktop={srcDesktop || src}
          className={classNames(styles.image)}
          alt={key_image[0].alt_text}
          innerTransition={{
            type: 'parallax',
            config: {
              speed: -2,
            },
          }}
        />
      </div>
    );
  }

  return (
    <div ref={elementRef} className={classNames(styles.container, className)}>
      <header className={styles.articleHeader}>
        <Heading
          ref={categoryRef}
          as="span"
          size="label-large"
          transitionInType="splitLineRotateIn"
          className={styles.newsLabel}
        >
          {getNewsCategoryLabel(categoryLabels, blok.category)}
        </Heading>
        <Heading
          ref={titleRef}
          as="h1"
          size="heading-03"
          transitionInType="splitCharsSlideInUp"
          className={styles.title}
        >
          {blok.title}
        </Heading>
        {blok?.publish_date && (
          <Text
            ref={dateRef}
            as="time"
            size="body-standard"
            transitionInType="splitLineRotateIn"
            className={styles.publishDate}
          >
            {formattedDate}
          </Text>
        )}
        {blok?.pardot_id && blok.pardot_download_label && (
          <PrimaryButton
            ref={headerButtonRef}
            className={classNames(styles.headerButton)}
            label={blok.pardot_download_label}
            onClick={() => {
              const element = document?.querySelector(`[data-scroll-to=download-pdf]`);
              if (element) {
                scrollIntoView(element);
              }
            }}
          />
        )}
        <hr ref={lineRef} />
        <div className={styles.shareSection}>
          <Text
            ref={shareTitleRef}
            as="p"
            size="body-standard"
            transitionInType="slideUpFadeIn"
            className={styles.shareText}
          >
            {globalSettings.label_news_share}
          </Text>
          <span className={styles.copySection}>
            <Text
              as="p"
              size="body-standard"
              transitionInType="none"
              className={styles.linkCopied}
              style={{ opacity: hasCopiedLink ? '1' : '0' }}
            >
              Link copied!
            </Text>
            {isClientOnly && (navigator?.share || navigator?.clipboard) && (
              <IconButton
                ref={buttonRef}
                icon={isMobileViewport ? 'upload' : 'link'}
                onClick={shareWebsite}
                isVisible={{
                  onMount: true,
                  value: true,
                }}
                aria-label={isMobileViewport ? 'Share link' : 'Copy link'}
                title={isMobileViewport ? 'Share link' : 'Copy link'}
              />
            )}
          </span>
        </div>
      </header>

      <figure className={styles.titleImage}>
        {media}

        {key_image?.[0]?.image_caption && (
          <Text as="figcaption" size="body-standard" transitionInType="none">
            {key_image[0].image_caption}
          </Text>
        )}
      </figure>
    </div>
  );
}

ArticleHeader.displayName = 'ArticleHeader';

export default ArticleHeader;
